import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { cn } from '../../../utils/cn';

const DemandDetailPanel = ({
  demand,
  technicians,
  onApprove,
  onReject,
  onAssignTechnician,
  onCreateProject,
  onSendMessage
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState('');

  if (!demand) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="MousePointerClick" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          Selecione uma demanda
        </h3>
        <p className="text-sm text-muted-foreground">
          Clique em uma demanda da tabela para ver os detalhes completos e opções de ação.
        </p>
      </div>
    );
  }

  const handleReject = () => {
    if (rejectionReason?.trim()) {
      onReject(demand?.id, rejectionReason);
      setRejectionReason('');
      setShowRejectionForm(false);
    }
  };

  const handleAssign = () => {
    if (selectedTechnician) {
      onAssignTechnician(demand?.id, selectedTechnician);
      setSelectedTechnician('');
    }
  };

  const handleSendMessage = () => {
    if (message?.trim()) {
      onSendMessage(demand?.id, message);
      setMessage('');
    }
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return 'FileText';
    if (type?.includes('image')) return 'Image';
    if (type?.includes('word')) return 'FileText';
    return 'File';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden sticky top-4">
      {/* Header */}
      <div className="bg-muted/50 px-6 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          Detalhes da Demanda
        </h2>
      </div>
      <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
        {/* Demand Code & Title */}
        <div>
          <div className="text-sm text-muted-foreground mb-1">
            {demand?.code}
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            {demand?.title}
          </h3>
          <p className="text-sm text-muted-foreground">
            {demand?.description}
          </p>
        </div>

        {/* Client Information */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start space-x-3 mb-3">
            <img
              src={demand?.client?.avatar}
              alt={`Avatar de ${demand?.client?.name}`}
              className="w-12 h-12 rounded-full"
            />
            <div className="flex-1">
              <div className="font-medium text-foreground">
                {demand?.client?.name}
              </div>
              <div className="text-sm text-muted-foreground">
                {demand?.client?.contact}
              </div>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="Mail" size={14} />
              <span>{demand?.client?.email}</span>
            </div>
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="Phone" size={14} />
              <span>{demand?.client?.phone}</span>
            </div>
          </div>
        </div>

        {/* Demand Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Tipo</div>
            <div className="text-sm font-medium text-foreground">
              {demand?.requestType}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Prioridade</div>
            <div className="text-sm font-medium text-foreground">
              {demand?.priority}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Data</div>
            <div className="text-sm font-medium text-foreground">
              {demand?.submissionDate}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Status</div>
            <div className="text-sm font-medium text-foreground">
              {demand?.status}
            </div>
          </div>
          {demand?.slaDeadline && (
            <div className="col-span-2">
              <div className="text-xs text-muted-foreground mb-1">Prazo SLA</div>
              <div className="text-sm font-medium text-foreground">
                {demand?.slaDeadline}
              </div>
            </div>
          )}
        </div>

        {/* Assigned Technician */}
        {demand?.assignedTechnician && (
          <div className="bg-primary/5 rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-2">
              Técnico Atribuído
            </div>
            <div className="flex items-center space-x-3">
              <img
                src={demand?.assignedTechnician?.avatar}
                alt={`Avatar de ${demand?.assignedTechnician?.name}`}
                className="w-8 h-8 rounded-full"
              />
              <div>
                <div className="text-sm font-medium text-foreground">
                  {demand?.assignedTechnician?.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {demand?.assignedTechnician?.availability}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Attachments */}
        {demand?.attachments && demand?.attachments?.length > 0 && (
          <div>
            <div className="text-sm font-medium text-foreground mb-3">
              Anexos ({demand?.attachments?.length})
            </div>
            <div className="space-y-2">
              {demand?.attachments?.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <Icon name={getFileIcon(file?.type)} size={20} className="text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {file?.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {file?.size}
                      </div>
                    </div>
                  </div>
                  <Icon name="Download" size={16} className="text-muted-foreground flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {demand?.status === 'Pendente' && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-foreground mb-2">
              Ações Rápidas
            </div>
            <Button
              variant="success"
              iconName="Check"
              iconPosition="left"
              fullWidth
              onClick={() => onApprove(demand?.id)}
            >
              Aprovar Demanda
            </Button>
            <Button
              variant="danger"
              iconName="X"
              iconPosition="left"
              fullWidth
              onClick={() => setShowRejectionForm(!showRejectionForm)}
            >
              Rejeitar Demanda
            </Button>

            {showRejectionForm && (
              <div className="space-y-3 p-4 bg-error/5 rounded-lg border border-error/20">
                <Input
                  placeholder="Motivo da rejeição..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                />
                <div className="flex space-x-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleReject}
                    disabled={!rejectionReason?.trim()}
                  >
                    Confirmar Rejeição
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowRejectionForm(false);
                      setRejectionReason('');
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Technician Assignment */}
        {(demand?.status === 'Aprovado' || demand?.status === 'Em Análise') && !demand?.assignedTechnician && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-foreground">
              Atribuir Técnico
            </div>
            <Select
              value={selectedTechnician}
              onValueChange={setSelectedTechnician}
              placeholder="Selecione um técnico"
            >
              {technicians?.filter(t => t?.availability !== 'Férias')?.map((tech) => (
                <option key={tech?.id} value={tech?.id}>
                  {tech?.name} - {tech?.availability} ({tech?.workload} OSs)
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              iconName="UserPlus"
              iconPosition="left"
              fullWidth
              onClick={handleAssign}
              disabled={!selectedTechnician}
            >
              Atribuir Técnico
            </Button>
          </div>
        )}

        {/* Create Project */}
        {demand?.status === 'Aprovado' && (
          <Button
            variant="default"
            iconName="FolderPlus"
            iconPosition="left"
            fullWidth
            onClick={() => onCreateProject(demand?.id)}
          >
            Criar Projeto
          </Button>
        )}

        {/* Communication */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">
            Comunicação com Cliente
          </div>
          <Input
            placeholder="Digite uma mensagem..."
            value={message}
            onChange={(e) => setMessage(e?.target?.value)}
          />
          <Button
            variant="outline"
            iconName="Send"
            iconPosition="left"
            fullWidth
            onClick={handleSendMessage}
            disabled={!message?.trim()}
          >
            Enviar Mensagem
          </Button>
        </div>

        {/* History */}
        <div>
          <div className="text-sm font-medium text-foreground mb-3">
            Histórico
          </div>
          <div className="space-y-3">
            {demand?.history?.map((item, index) => (
              <div key={index} className="flex space-x-3">
                <div className="flex-shrink-0">
                  <div className={cn(
                    "w-2 h-2 rounded-full mt-2",
                    item?.type === 'creation' && "bg-primary",
                    item?.type === 'approval' && "bg-success",
                    item?.type === 'rejection' && "bg-error",
                    item?.type === 'assignment' && "bg-warning"
                  )}></div>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-foreground">
                    {item?.action}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {item?.date} - {item?.user}
                  </div>
                  {item?.note && (
                    <div className="text-xs text-muted-foreground mt-1 italic">
                      "{item?.note}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rejection Reason Display */}
        {demand?.status === 'Rejeitado' && demand?.rejectionReason && (
          <div className="bg-error/5 rounded-lg p-4 border border-error/20">
            <div className="text-sm font-medium text-error mb-2">
              Motivo da Rejeição
            </div>
            <p className="text-sm text-muted-foreground">
              {demand?.rejectionReason}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemandDetailPanel;